import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { authFetch } from '@/utils/auth-fetch';
import { FunnelSection, type FunnelData } from '@/components/generation-report/FunnelSection';
import { RuleImpactSection } from '@/components/generation-report/RuleImpactSection';
import { ProductDrillDown } from '@/components/generation-report/ProductDrillDown';
import { Skeleton } from '@/components/ui/skeleton';

interface RuleImpactEntry {
  ruleFamily: string;
  traceType: string;
  callSite: string | null;
  rowCount: number;
  aggregates: Record<string, number>;
}

interface ReportData {
  funnel: FunnelData;
  ruleImpact: RuleImpactEntry[];
}

export function GenerationReport() {
  const { runId } = useParams<{ runId: string }>();
  const [data, setData] = useState<ReportData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!runId) {
      setLoading(false);
      setError('Missing run ID');
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    authFetch(`/feed-imports/runs/${runId}/report`)
      .then(async (res) => {
        if (cancelled) return;
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || 'Request failed');
        }
        const json: ReportData = await res.json();
        setData(json);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Could not load the report');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [runId]);

  if (loading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6">
        <p className="text-destructive">Could not load the report{error ? `: ${error}` : ''}.</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold">Generation Report</h1>
      <FunnelSection funnel={data.funnel} />
      <RuleImpactSection ruleImpact={data.ruleImpact} />
      {runId && <ProductDrillDown runId={runId} />}
    </div>
  );
}
